import type { Id, ScriptDocument } from "@supermanus/document-model";
import { listEpisodes } from "@supermanus/document-model";

const PAGE_CSS = (landscape: boolean, repeatSceneHead: boolean) => `
@page {
  size: A4 ${landscape ? "landscape" : "portrait"};
  margin: ${repeatSceneHead ? "28mm" : "16mm"} 12mm 18mm 12mm;
}
.pagedjs_margin-top {
  position: relative;
  ${repeatSceneHead ? "align-items: start;" : ""}
}
.pagedjs_margin-top-left .pagedjs_margin-content,
.pagedjs_margin-top-right .pagedjs_margin-content {
  font-size: 8pt;
  line-height: 1.3;
  color: #6b6560;
}
.pagedjs_margin-top-left strong {
  color: #1a1a1a;
  font-weight: 600;
}
.pagedjs_pagebox .pagedjs_margin-top-left,
.pagedjs_pagebox .pagedjs_margin-top-right,
.pagedjs_pagebox .pagedjs_margin-top-center {
  ${repeatSceneHead ? "align-items: flex-start;" : ""}
}
.sm-print-scene-continued-head {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 1.5mm;
  font-size: 8.5pt;
  font-weight: 600;
  line-height: 1.3;
  color: #1a1a1a;
  background: #dce8e2;
  padding: 0.18rem 0.5rem;
  border-radius: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.sm-print-scene-head,
.sm-print-outline-top {
  /* Keep the whole heading box on one page (incl. wrapped title lines). */
  break-inside: avoid;
  page-break-inside: avoid;
  break-after: avoid;
  page-break-after: avoid;
}
.sm-print-scene {
  break-inside: auto;
  page-break-inside: auto;
}
.sm-print-table tr {
  break-inside: avoid;
  page-break-inside: avoid;
}
@page {
  @bottom-center {
    content: "Side " counter(page) " / " counter(pages);
    font-size: 8pt;
    color: #6b6560;
  }
}
.sm-print-break-before {
  break-before: page;
  page-break-before: always;
}
.sm-status-bar.is-compact .sm-status-step {
  padding: 0.35rem 0.1rem 0.45rem;
}
.sm-status-bar.is-compact .sm-status-step-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  overflow-wrap: normal;
  overflow: visible;
  max-width: none;
  max-height: none;
  letter-spacing: 0.04em;
  line-height: 1.1;
}
.sm-print-timing-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.sm-print-timing-table th.sm-print-timing-dur,
.sm-print-timing-table td.sm-print-timing-dur {
  width: 6.75rem;
  text-align: right;
  padding-right: 0.4rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
`;

export function suggestedPdfFilename(
  doc: ScriptDocument,
  episodeId?: Id | null,
): string {
  const episodes = listEpisodes(doc);
  const episode =
    (episodeId && doc.episodes[episodeId]) || episodes[0] || null;
  const base = [doc.production.title, episode?.title]
    .filter(Boolean)
    .join(" – ")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .trim();
  return `${base || "udskrift"}.pdf`;
}

export function clearPagedInsertedStyles() {
  for (const el of document.querySelectorAll(
    "style[data-pagedjs-inserted-styles]",
  )) {
    el.remove();
  }
}

let paginateQueue: Promise<void> = Promise.resolve();

function keepLastPageSet(renderTo: HTMLElement) {
  const areas = [...renderTo.querySelectorAll(":scope > .pagedjs_pages")];
  for (const extra of areas.slice(0, -1)) extra.remove();
}

function takeRunningHead(html: string): { body: string; head: string | null } {
  const box = document.createElement("div");
  box.innerHTML = html;
  const el = box.querySelector(".sm-print-running-head");
  if (!el) return { body: html, head: null };
  const head = el.outerHTML;
  el.remove();
  return { body: box.innerHTML, head };
}

function stampRunningHead(renderTo: HTMLElement, headHtml: string) {
  const parsed = document.createElement("div");
  parsed.innerHTML = headHtml;
  const title = parsed.querySelector("strong");
  const meta = parsed.querySelector("span");
  if (!title && !meta) return;
  const area = [...renderTo.querySelectorAll(":scope > .pagedjs_pages")].at(-1);
  if (!area) return;
  for (const page of area.querySelectorAll(":scope > .pagedjs_page")) {
    const fill = (loc: "left" | "right", html: string) => {
      const margin = page.querySelector(`.pagedjs_margin-top-${loc}`);
      const slot = margin?.querySelector(":scope > .pagedjs_margin-content");
      if (!margin || !slot || slot.querySelector("strong, span")) return;
      slot.insertAdjacentHTML("afterbegin", html);
      margin.classList.add("hasContent");
    };
    if (title) fill("left", title.outerHTML);
    if (meta) fill("right", meta.outerHTML);
    const group = page.querySelector<HTMLElement>(".pagedjs_margin-top");
    if (!group) continue;
    group.style.gridTemplateColumns =
      title && meta
        ? "minmax(0,1fr) 0 minmax(0,1fr)"
        : title
          ? "1fr 0 0"
          : "0 0 1fr";
  }
}

function pageStartsWithSceneHead(page: Element): boolean {
  const content = page.querySelector(".pagedjs_page_content");
  if (!content) return false;
  const head = content.querySelector("[data-scene-running]");
  if (!head) return false;
  const range = document.createRange();
  range.setStart(content, 0);
  range.setEndBefore(head);
  return !range.toString().replace(/\s+/g, "");
}

function sceneHeadBackground(el: Element): string {
  let node: HTMLElement | null = el as HTMLElement;
  while (node) {
    const styled = node.style.backgroundColor?.trim();
    if (styled) return styled;
    const computed = getComputedStyle(node).backgroundColor;
    if (
      computed &&
      computed !== "rgba(0, 0, 0, 0)" &&
      computed !== "transparent"
    ) {
      return computed;
    }
    if (node.classList.contains("pagedjs_page_content")) break;
    node = node.parentElement;
  }
  return "";
}

function stampContinuationSceneHeads(renderTo: HTMLElement) {
  const area = [...renderTo.querySelectorAll(":scope > .pagedjs_pages")].at(-1);
  if (!area) return;
  let lastLabel = "";
  let lastBg = "";
  for (const page of area.querySelectorAll(":scope > .pagedjs_page")) {
    const content = page.querySelector(".pagedjs_page_content");
    const heads = [
      ...(content?.querySelectorAll("[data-scene-running]") ?? []),
    ];
    const startsWithHead = pageStartsWithSceneHead(page);
    if (!startsWithHead && lastLabel) {
      const top = page.querySelector(".pagedjs_margin-top");
      if (top && !top.querySelector(".sm-print-scene-continued-head")) {
        const bar = document.createElement("div");
        bar.className = "sm-print-scene-continued-head";
        bar.textContent = `${lastLabel} (fortsat)`;
        if (lastBg) bar.style.backgroundColor = lastBg;
        top.appendChild(bar);
      }
    }
    if (heads.length) {
      const latest = heads[heads.length - 1]!;
      lastLabel =
        latest.getAttribute("data-scene-running") || lastLabel;
      lastBg = sceneHeadBackground(latest) || lastBg;
    }
  }
}

function pageContentEl(page: Element): Element | null {
  return page.querySelector(".pagedjs_page_content");
}

function pageBodyText(page: Element): string {
  return pageContentEl(page)?.textContent?.replace(/\s+/g, "") ?? "";
}

/** True when the page has printable body content (not just running head/foot). */
function pageHasBody(page: Element): boolean {
  const content = pageContentEl(page);
  if (!content) return false;
  if (content.querySelector("img, svg, table, canvas")) return true;
  return pageBodyText(page).length > 0;
}

function dropDuplicateOrEmptyFirstPage(renderTo: HTMLElement) {
  const area = [...renderTo.querySelectorAll(":scope > .pagedjs_pages")].at(-1);
  if (!area) return;
  const pages = [...area.querySelectorAll(":scope > .pagedjs_page")];
  if (pages.length < 2) return;
  const first = pages[0]!;
  const t0 = pageBodyText(first);
  const t1 = pageBodyText(pages[1]!);
  if (!pageHasBody(first) || (t0.length > 0 && t0 === t1)) first.remove();
}

/**
 * pagedjs (and break-before / avoid rules) can leave a trailing empty sheet.
 * Drop those so preview, PDF and browser print stay in sync.
 */
function dropEmptyTrailingPages(renderTo: HTMLElement) {
  const area = [...renderTo.querySelectorAll(":scope > .pagedjs_pages")].at(-1);
  if (!area) return;
  const pages = [...area.querySelectorAll(":scope > .pagedjs_page")];
  if (pages.length < 2) return;
  for (let i = pages.length - 1; i >= 1; i--) {
    const page = pages[i]!;
    if (pageHasBody(page)) break;
    page.remove();
  }
}

export async function paginatePrintHtml(
  html: string,
  renderTo: HTMLElement,
  landscape: boolean,
  options: { repeatSceneHead?: boolean } = {},
): Promise<void> {
  const repeatSceneHead = Boolean(options.repeatSceneHead);
  const job = paginateQueue.then(async () => {
    renderTo.replaceChildren();
    clearPagedInsertedStyles();
    const { Previewer } = (await import("pagedjs")) as {
      Previewer: new () => {
        preview: (
          content: string,
          stylesheets: unknown[],
          renderTo: HTMLElement,
        ) => Promise<unknown>;
      };
    };
    const { body, head } = takeRunningHead(html);
    /* Only @page CSS — never the app stylesheet. pagedjs lifts @media print onto
       the screen, which would hide .sm-no-print (the toolbar) and collapse pages.
       renderTo must be in the document at real A4 size (no zoom) so page boxes
       can be measured. Never paginate into a detached node.
       Do not pass position:running() as the first flow child — pagedjs duplicates
       page 1. Stamp the header into each top margin after layout instead. */
    await new Previewer().preview(
      body,
      [{ "minproduktion-paged": PAGE_CSS(landscape, repeatSceneHead) }],
      renderTo,
    );
    keepLastPageSet(renderTo);
    dropDuplicateOrEmptyFirstPage(renderTo);
    dropEmptyTrailingPages(renderTo);
    if (head) stampRunningHead(renderTo, head);
    if (repeatSceneHead) stampContinuationSceneHeads(renderTo);
    // Continuation heads can land on a page we then treat as empty — prune again.
    dropEmptyTrailingPages(renderTo);
  });
  paginateQueue = job.then(
    () => undefined,
    () => undefined,
  );
  return job;
}

export async function pdfFromPagedRoot(
  root: HTMLElement,
  landscape: boolean,
  onProgress?: (done: number, total: number) => void,
): Promise<Blob> {
  const area = [...root.querySelectorAll(".pagedjs_pages")].at(-1);
  const pages = [
    ...(area?.querySelectorAll<HTMLElement>(":scope > .pagedjs_page") ?? []),
  ].filter((page) => pageHasBody(page));
  if (pages.length === 0) {
    throw new Error("Ingen sider at gemme");
  }
  const html2canvas = (await import("html2canvas")).default;
  const { jsPDF } = await import("jspdf");
  const pageW = landscape ? 297 : 210;
  const pageH = landscape ? 210 : 297;
  const pdf = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]!;
    page.setAttribute("data-sm-pdf-page", String(i));
    onProgress?.(i + 1, pages.length);
    const canvas = await html2canvas(page, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
      ignoreElements: (el) =>
        el.classList.contains("sm-print-source") ||
        el.classList.contains("sm-print-toolbar") ||
        el.classList.contains("sm-no-print"),
      onclone: (cloned) => {
        for (const el of cloned.querySelectorAll(
          ".sm-no-print, .sm-print-source, .sm-print-toolbar",
        )) {
          (el as HTMLElement).style.display = "none";
        }
        const clonePage = cloned.querySelector(
          `[data-sm-pdf-page="${i}"]`,
        ) as HTMLElement | null;
        if (clonePage) {
          clonePage.style.boxShadow = "none";
          clonePage.style.outline = "none";
        }
      },
    });
    const img = canvas.toDataURL("image/jpeg", 0.92);
    if (i > 0) pdf.addPage();
    pdf.addImage(img, "JPEG", 0, 0, pageW, pageH);
  }
  return pdf.output("blob");
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4_000);
}
